import {
  CheckCircleOutline as CompletedIcon,
  Groups as CustomersIcon,
  LocalFireDepartment as BusiestIcon,
  PhoneMissed as ContactAttemptIcon,
  TouchApp as TouchIcon,
} from "@mui/icons-material";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import React from "react";
import { FunFacts } from "../../store/services/salesManagerService";

const CONTACT_ATTEMPT_COLOR = "#9B6B00";

interface FunFactsBandProps {
  funFacts: FunFacts;
}

const formatBusiestDay = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "d MMM", { locale: sv });
  } catch {
    return iso;
  }
};

const FunFactsBand: React.FC<FunFactsBandProps> = ({ funFacts }) => {
  const theme = useTheme();

  const facts = [
    {
      icon: <TouchIcon />,
      color: theme.palette.primary.main,
      value: funFacts.TotalTouches.toLocaleString("sv-SE"),
      label: "Aktiviteter",
    },
    {
      icon: <CompletedIcon />,
      color: theme.palette.success.main,
      value: funFacts.TotalCompleted.toLocaleString("sv-SE"),
      label: "Genomförda",
    },
    {
      icon: <ContactAttemptIcon />,
      color: CONTACT_ATTEMPT_COLOR,
      value: funFacts.TotalContactAttempts.toLocaleString("sv-SE"),
      label: "Kontaktförsök",
    },
    {
      icon: <CustomersIcon />,
      color: theme.palette.info.main,
      value: funFacts.UniqueCustomers.toLocaleString("sv-SE"),
      label: "Unika kunder",
    },
    {
      icon: <BusiestIcon />,
      color: theme.palette.error.main,
      value: formatBusiestDay(funFacts.BusiestDay),
      label: funFacts.BusiestDay
        ? `Tuffaste dagen · ${funFacts.BusiestDayCount}`
        : "Tuffaste dagen",
    },
  ];

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      divider={
        <Box
          sx={{
            borderLeft: `1px solid ${theme.palette.divider}`,
            display: { xs: "none", sm: "block" },
          }}
        />
      }
      sx={{ width: "100%" }}
    >
      {facts.map((fact) => (
        <Stack
          key={fact.label}
          direction="row"
          spacing={1.5}
          alignItems="center"
          flex={1}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(fact.color, 0.12),
              color: fact.color,
              flexShrink: 0,
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          >
            {fact.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>
              {fact.value}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
              noWrap
            >
              {fact.label}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

export default FunFactsBand;
